import React, { useState, useEffect, useRef } from 'react';
import { Phone, Info, CheckCircle, AlertCircle, Moon, Sun, DollarSign, CreditCard, User, FileText } from 'lucide-react';

/* ─── Modal ─────────────────────────────────────── */
const Modal = ({ isOpen, onClose, type, message }) => {
    if (!isOpen) return null;
    return (
        <div className="trf-modal-overlay" role="dialog" aria-modal="true" aria-label={type === 'success' ? 'Success' : 'Error'}>
            <div className="trf-modal-box">
                <div className={`trf-modal-icon ${type}`}>
                    {type === 'success'
                        ? <CheckCircle size={52} />
                        : <AlertCircle size={52} />}
                </div>
                <p className="trf-modal-title">{type === 'success' ? 'Payment Successful!' : 'Payment Failed'}</p>
                <p className="trf-modal-message">{message}</p>
                <button className="trf-modal-close" onClick={onClose}>Close</button>
            </div>
        </div>
    );
};

/* ─── Main Form ──────────────────────────────────── */
export default function PaymentForm() {
    const [darkMode, setDarkMode]       = useState(false);
    const [modalState, setModalState]   = useState({ isOpen: false, type: 'success', message: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const cloverRef = useRef(null);
    const elementsRef = useRef(null);
    const fieldsRef = useRef(null);

    const [formData, setFormData] = useState({
        // Patient Information
        patientName:       '',
        patientEmail:      '',
        patientPhone:      '',
        patientDOB:        '',
        patientId:         '',
        notes:             '',
        
        // Payment Information
        amount:            '',
        procedureType:     '',
        doctorName:        '',
        invoiceNumber:     '',
    });

    const procedureList = [
        'Consultation', 'Wisdom Teeth Removal', 'Dental Implant', 
        'Surgery Deposit', 'Follow-up Visit', 'Insurance Copay', 'Other'
    ];

    const closeModal = () => setModalState(s => ({ ...s, isOpen: false }));

    const formatPhone = (val) => {
        const d = val.replace(/\D/g, '');
        if (d.length < 4) return d;
        if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
        return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6, 10)}`;
    };

    const handlePhone = (e, field) =>
        setFormData(prev => ({ ...prev, [field]: formatPhone(e.target.value) }));

    const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

    useEffect(() => {
        // Initialize Clover once the script is loaded and component mounted
        const initClover = () => {
            if (window.Clover && !cloverRef.current) {
                const clover = new window.Clover('32409f5393b44b032301e0e1b91bcb61', {
                    merchantId: '534703850108622'
                });
                cloverRef.current = clover;
                const elements = clover.elements();
                elementsRef.current = elements;

                const styles = {
                    body: {
                        fontFamily: 'Inter, sans-serif',
                        fontSize: '16px',
                        color: '#1a2c45',
                    },
                    input: {
                        padding: '0',
                    }
                };

                const cardNumber = elements.create('CARD_NUMBER', styles);
                const cardDate = elements.create('CARD_DATE', styles);
                const cardCvv = elements.create('CARD_CVV', styles);
                const cardPostal = elements.create('CARD_POSTAL_CODE', styles);
                const cardAddress = elements.create('CARD_STREET_ADDRESS', styles);

                cardNumber.mount('#clover-card-number');
                cardDate.mount('#clover-card-date');
                cardCvv.mount('#clover-card-cvv');
                cardPostal.mount('#clover-card-postal');
                cardAddress.mount('#clover-card-address');
                
                fieldsRef.current = { cardNumber, cardDate, cardCvv, cardPostal, cardAddress };

                const displayError = document.getElementById('clover-card-errors');
                const handleChange = (event) => {
                    if (event.error) {
                        displayError.textContent = event.error.message;
                    } else {
                        displayError.textContent = '';
                    }
                };
                cardNumber.addEventListener('change', handleChange);
                cardDate.addEventListener('change', handleChange);
                cardCvv.addEventListener('change', handleChange);
                cardPostal.addEventListener('change', handleChange);
                cardAddress.addEventListener('change', handleChange);
            }
        };

        // Check immediately or wait for script load if it's slow
        if (window.Clover) {
            initClover();
        } else {
            // Simple poll in case script is still downloading
            const timer = setInterval(() => {
                if (window.Clover) {
                    initClover();
                    clearInterval(timer);
                }
            }, 200);
            return () => clearInterval(timer);
        }
    }, []);

    // Sync Clover iframe text color with Dark Mode
    useEffect(() => {
        if (fieldsRef.current) {
            const color = darkMode ? '#ffffff' : '#1a2c45';
            const newStyles = { body: { color } };
            Object.values(fieldsRef.current).forEach(field => {
                if (field && typeof field.update === 'function') {
                    // Provide fallback object formats to ensure compatibility with Clover SDK
                    field.update({ styles: newStyles }).catch(() => {});
                }
            });
        }
    }, [darkMode]);

    const handleSubmit = async () => {
        if (isSubmitting) return;
        
        // Validation
        if (!formData.patientName || !formData.patientEmail || !formData.patientPhone) {
            setModalState({ isOpen: true, type: 'error', message: 'Please fill in required fields: Name, Email, and Phone.' });
            const firstEmpty = !formData.patientName ? 'patName' : (!formData.patientEmail ? 'patEmail' : 'patPhone');
            document.getElementById(firstEmpty)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        const numericAmount = parseFloat(formData.amount.toString().replace(/,/g, ''));
        if (isNaN(numericAmount) || numericAmount < 1 || numericAmount > 10000) {
            setModalState({ isOpen: true, type: 'error', message: 'Please enter a valid amount between $1 and $10,000.' });
            document.getElementById('payAmount')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        if (!formData.procedureType) {
            setModalState({ isOpen: true, type: 'error', message: 'Please select a procedure type.' });
            document.getElementById('procType')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }

        setIsSubmitting(true);
        try {
            if (!cloverRef.current) {
                throw new Error("Payment system is still loading. Please try again in a moment.");
            }

            // Add a timeout to prevent infinite hanging if Clover SDK fails to communicate (e.g. adblockers)
            const tokenPromise = cloverRef.current.createToken();
            const timeoutPromise = new Promise((_, reject) => 
                setTimeout(() => reject(new Error("Payment system timed out. Please check your browser's privacy settings or try another browser.")), 15000)
            );
            
            const result = await Promise.race([tokenPromise, timeoutPromise]);
            
            if (result.errors) {
                const errorKey = Object.keys(result.errors)[0];
                setModalState({ isOpen: true, type: 'error', message: result.errors[errorKey] });
                setIsSubmitting(false);
                return;
            }

            const response = await fetch('/api/payments', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    source: result.token,
                    amount: numericAmount,
                    patientName: formData.patientName,
                    patientEmail: formData.patientEmail,
                    patientPhone: formData.patientPhone,
                    procedureType: formData.procedureType,
                    doctorName: formData.doctorName,
                    invoiceNumber: formData.invoiceNumber,
                    notes: formData.notes
                })
            });

            if (!response.ok) {
                let errorMessage = 'Payment processing failed.';
                try {
                    const errData = await response.json();
                    errorMessage = errData.message || errorMessage;
                } catch {
                    // Backend returned non-JSON (like 504 Gateway Timeout or 500 error page)
                    if (response.status === 504) errorMessage = 'The server took too long to respond. Please try again.';
                    else errorMessage = `Server error (${response.status}). Please try again later.`;
                }
                throw new Error(errorMessage);
            }
            
            setModalState({ isOpen: true, type: 'success', message: 'Your payment has been successfully processed.' });
            setFormData({
                patientName: '', patientEmail: '', patientPhone: '', patientDOB: '', patientId: '', notes: '',
                amount: '', procedureType: '', doctorName: '', invoiceNumber: ''
            });
            // We would ideally reset the Clover elements here, but for simplicity a success screen handles the end of flow
        } catch (err) {
            setModalState({ isOpen: true, type: 'error', message: err.message || 'Unable to process payment. Please try again.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className={`app${darkMode ? ' dark' : ''}`}>
            <Modal
                isOpen={modalState.isOpen}
                onClose={closeModal}
                type={modalState.type}
                message={modalState.message}
            />

            {/* ── Dark Mode Toggle ── */}
            <button
                className="trf-theme-toggle"
                onClick={() => setDarkMode(d => !d)}
                aria-label="Toggle dark mode"
            >
                {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>

            {/* ── Header ── */}
            <header className="trf-header">
                <div style={{ position: 'relative', zIndex: 1 }}>
                    <div className="trf-header-logo-text">
                        <span style={{ color: '#ffffff' }}>Titanium</span> <span>Surgical Arts</span>
                    </div>
                    <p className="mt-3 mb-0" style={{ fontSize: '1rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>
                        Secure Payment Gateway
                    </p>
                </div>
                <div className="trf-header-divider" />
            </header>

            {/* ── Body ── */}
            <div className="trf-container">

                {/* ── Patient Information ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <User size={14} /> Patient Information
                    </h2>
                    <div className="trf-grid-3 mb-3">
                        <div>
                            <label htmlFor="patName" className="trf-label">
                                Full Name <span className="trf-required">*</span>
                            </label>
                            <input
                                id="patName" type="text" className="trf-input"
                                placeholder="Jane Doe"
                                value={formData.patientName}
                                onChange={e => set('patientName', e.target.value)}
                                aria-required="true"
                            />
                        </div>
                        <div>
                            <label htmlFor="patEmail" className="trf-label">
                                Email <span className="trf-required">*</span>
                            </label>
                            <input
                                id="patEmail" type="email" className="trf-input"
                                placeholder="jane@example.com"
                                value={formData.patientEmail}
                                onChange={e => set('patientEmail', e.target.value)}
                                aria-required="true"
                            />
                        </div>
                        <div>
                            <label htmlFor="patPhone" className="trf-label">
                                Phone Number <span className="trf-required">*</span>
                            </label>
                            <input
                                id="patPhone" type="tel" className="trf-input"
                                placeholder="(555) 987-6543"
                                value={formData.patientPhone}
                                onChange={e => handlePhone(e, 'patientPhone')}
                                aria-required="true"
                            />
                        </div>
                    </div>
                    <div className="trf-grid-3 mb-3">
                        <div>
                            <label htmlFor="patDOB" className="trf-label">Date of Birth (Optional)</label>
                            <input
                                id="patDOB" type="date" className="trf-input"
                                value={formData.patientDOB}
                                onChange={e => set('patientDOB', e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="patId" className="trf-label">Patient ID (Optional)</label>
                            <input
                                id="patId" type="text" className="trf-input"
                                placeholder="e.g. 12345"
                                value={formData.patientId}
                                onChange={e => set('patientId', e.target.value)}
                            />
                        </div>
                    </div>
                    <div>
                        <label htmlFor="patNotes" className="trf-label">Notes (Optional)</label>
                        <textarea
                            id="patNotes" className="trf-input"
                            rows={2}
                            placeholder="Additional patient notes..."
                            value={formData.notes}
                            onChange={e => set('notes', e.target.value)}
                            style={{ resize: 'vertical' }}
                        />
                    </div>
                </section>

                {/* ── Payment Information ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <DollarSign size={14} /> Payment Details
                    </h2>
                    <div className="trf-grid-2 mb-3">
                        <div>
                            <label htmlFor="payAmount" className="trf-label">
                                Custom Amount <span className="trf-required">*</span>
                            </label>
                            <div style={{ position: 'relative' }}>
                                <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--gray-600)', fontWeight: 'bold' }}>$</span>
                                <input
                                    id="payAmount" type="text" className="trf-input"
                                    placeholder="250.00"
                                    value={formData.amount}
                                    onChange={e => {
                                        const val = e.target.value.replace(/[^0-9.]/g, '');
                                        set('amount', val);
                                    }}
                                    onBlur={() => {
                                        if (!formData.amount) return;
                                        const num = parseFloat(formData.amount.toString().replace(/,/g, ''));
                                        if (!isNaN(num)) {
                                            set('amount', num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
                                        }
                                    }}
                                    aria-required="true"
                                    style={{ paddingLeft: '28px' }}
                                />
                            </div>
                            <small style={{ color: 'var(--gray-400)', fontSize: '0.75rem', display: 'block', marginTop: '4px' }}>Min: $1 | Max: $10,000</small>
                        </div>
                        <div>
                            <label htmlFor="procType" className="trf-label">
                                Procedure Type <span className="trf-required">*</span>
                            </label>
                            <select
                                id="procType" className="trf-input"
                                value={formData.procedureType}
                                onChange={e => set('procedureType', e.target.value)}
                                aria-required="true"
                                style={{ appearance: 'auto' }}
                            >
                                <option value="" disabled>Select a procedure</option>
                                {procedureList.map(p => (
                                    <option key={p} value={p}>{p}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="trf-grid-2">
                        <div>
                            <label htmlFor="docName" className="trf-label">Doctor / Surgeon Name</label>
                            <input
                                id="docName" type="text" className="trf-input"
                                placeholder="Dr. Tabarini"
                                value={formData.doctorName}
                                onChange={e => set('doctorName', e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="invNum" className="trf-label">Invoice Number</label>
                            <input
                                id="invNum" type="text" className="trf-input"
                                placeholder="e.g. INV-1002"
                                value={formData.invoiceNumber}
                                onChange={e => set('invoiceNumber', e.target.value)}
                            />
                        </div>
                    </div>
                </section>

                {/* ── Credit Card (Clover Hosted Fields) ── */}
                <section className="trf-card">
                    <h2 className="trf-section-title">
                        <CreditCard size={14} /> Credit Card Information
                    </h2>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
                        Please enter your credit card details below. All payments are processed securely via Clover.
                    </p>
                    
                    <div className="mb-3">
                        <label className="trf-label">Billing Street Address <span className="trf-required">*</span></label>
                        <div id="clover-card-address" className="trf-input clover-iframe-container" style={{ height: '44px', display: 'flex', alignItems: 'center' }}>
                        </div>
                    </div>
                    
                    <div className="mb-3">
                        <label className="trf-label">Card Number <span className="trf-required">*</span></label>
                        {/* Clover Hosted Field Placeholder */}
                        <div id="clover-card-number" className="trf-input clover-iframe-container" style={{ height: '44px', display: 'flex', alignItems: 'center' }}>
                            {/* The Clover SDK will inject the iframe here */}
                        </div>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label className="trf-label">Expiration Date <span className="trf-required">*</span></label>
                            <div id="clover-card-date" className="trf-input clover-iframe-container" style={{ height: '44px', display: 'flex', alignItems: 'center' }}>
                            </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                            <div>
                                <label className="trf-label">CVV <span className="trf-required">*</span></label>
                                <div id="clover-card-cvv" className="trf-input clover-iframe-container" style={{ height: '44px', display: 'flex', alignItems: 'center' }}>
                                </div>
                            </div>
                            <div>
                                <label className="trf-label">Zip / Postal <span className="trf-required">*</span></label>
                                <div id="clover-card-postal" className="trf-input clover-iframe-container" style={{ height: '44px', display: 'flex', alignItems: 'center' }}>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <div className="clover-errors" id="clover-card-errors" role="alert" style={{ color: '#e53e3e', fontSize: '0.85rem', marginTop: '0.5rem', minHeight: '20px' }}></div>
                </section>

                {/* ── Footer info ── */}
                <div className="trf-footer-info">
                    <p style={{ color: 'var(--gray-400)', fontSize: '0.75rem' }}>
                        Fields marked <span style={{ color: '#e53e3e' }}>*</span> are required.
                    </p>
                </div>

                {/* ── Submit ── */}
                <button
                    className="trf-btn-submit"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    aria-label="Submit payment"
                >
                    {isSubmitting ? (
                        <>
                            <span className="trf-spinner" />
                            Processing…
                        </>
                    ) : 'Pay Now'}
                </button>
            </div>
        </div>
    );
}
